import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { useSelector, useDispatch } from "react-redux";
import { getAccountBalance, payForResumeAnalysis, checkResumeAnalysisPayment, requestProfessionalResumeReview, invalidateResumeAnalysisSession } from "../../../redux/actions/walletActions";
import { getMyJobSeekerSubscription } from "../../../redux/actions/jobSeekerSubscriptionActions";
import { useHistory } from "react-router-dom";
import { numberWithCommas } from "../../../utils/helpers";
import httpRequest from "../../../ajax";
import logoImg from "../../../assets/icons/blueWBlogo.svg";
import "./ResumeAnalysisModal.css";

const ANALYSIS_COST_USD = 29.00;
const ANALYSIS_COST_NGN = 7999.00;

const BARE_AMP_RE = /&(?![a-zA-Z]{1,8};|#\d{1,6};|#x[0-9a-fA-F]{1,6};)/g;

const decodeHtml = (raw) => {
  if (!raw || typeof raw !== "string") return raw ?? "";
  let str = raw;
  try {
    const ta = document.createElement("textarea");
    ta.innerHTML = str;
    str = ta.value;
  } catch (_) {
    str = str
      .replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&apos;/g, "'")
      .replace(/&nbsp;/g, " ");
  }
  const bareAmps  = (str.match(BARE_AMP_RE) || []).length;
  const nonAmpLen = str.replace(BARE_AMP_RE, "").length;
  if (nonAmpLen > 0 && bareAmps / nonAmpLen >= 0.6) {
    str = str.replace(BARE_AMP_RE, "");
  }
  return str;
};

const deepDecode = (value) => {
  if (typeof value === "string") return decodeHtml(value);
  if (Array.isArray(value)) return value.map(deepDecode);
  if (value && typeof value === "object") {
    const out = {};
    for (const k of Object.keys(value)) out[k] = deepDecode(value[k]);
    return out;
  }
  return value;
};

const safe = (val) => decodeHtml(String(val ?? ""));

const cleanForPdf = (val) => {
  let s = String(val ?? "");
  s = s.replace(/[\u2018\u2019\u02BC\u2032]/g, "'");
  s = s.replace(/[\u201C\u201D\u2033]/g, '"');
  s = s.replace(/[\u2013\u2014\u2011\u2012\u2010]/g, '-');
  s = s.replace(/\u2026/g, '...');
  s = s.replace(/[\u2022\u25CF\u25E6\u2023]/g, '*');
  s = s.replace(/[^\u0020-\u007E\u00A0-\u00FF]/g, '');
  return s;
};

const ResumeAnalysisModal = ({ isOpen, onClose, analysisData, requiresPayment = true, analysisTier = null, onReAnalyze }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [hasPaid, setHasPaid] = useState(false);
  const [hasSubscriptionAccess, setHasSubscriptionAccess] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [requestingReview, setRequestingReview] = useState(false);
  const [reviewRequested, setReviewRequested] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);
  const [reAnalyzing, setReAnalyzing] = useState(false);
  const contentRef = useRef(null);
  const blurredRef = useRef(null);
  const paywallTriggered = useRef(false);
  const { balance } = useSelector((state) => state.wallet);
  const { userData } = useSelector((state) => state.auth);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);

  const [modalToast, setModalToast] = useState({ show: false, message: "", type: "info" });

  const showModalToast = (type = "info", message, duration = 4000) => {
    setModalToast({ show: true, message, type });
    setTimeout(() => setModalToast({ show: false, message: "", type: "info" }), duration);
  };

  const isNigerian = userData?.country == "160";
  const analysisCost = isNigerian ? ANALYSIS_COST_NGN : ANALYSIS_COST_USD;
  const currencySymbol = isNigerian ? "\u20A6" : "$";
  const currencyLabel = isNigerian ? "NGN" : "USD";

  // Fetch subscription data
  useEffect(() => {
    if (isOpen) {
      setLoadingSubscription(true);
      httpRequest("get", "/job-seeker-subscriptions/my-subscription")
        .then((response) => {
          if (response.data.status && response.data.data.subscription) {
            setSubscriptionData(response.data.data.subscription);
          }
        })
        .catch((error) => {
          console.log("No active subscription or error fetching:", error);
        })
        .finally(() => {
          setLoadingSubscription(false);
        });
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !loadingSubscription) {
      // Use analysisTier (passed from index.js at analysis time, before deep scan deduction)
      // This avoids the race condition where deep_scans_remaining is already 0 by the time modal fetches
      const tier = analysisTier || subscriptionData?.tier || "freemium";
      const isProHunter = tier === "pro_hunter";
      const isSprintPack = tier === "sprint_pack";
      
      // Pro Hunter: unlimited access, no payment required
      // Sprint Pack: analysisTier is set only when deep scan was available, so grant access
      // Freemium / Sprint Pack with no scans: require payment
      if (isProHunter) {
        setHasPaid(false);
        setHasSubscriptionAccess(true);
        setShowPaywall(false);
      } else if (isSprintPack && analysisTier) {
        // analysisTier is only set when the user had a deep scan available
        setHasPaid(false);
        setHasSubscriptionAccess(true);
        setShowPaywall(false);
      } else if (requiresPayment) {
        // Freemium users need to pay
        getAccountBalance(dispatch);
        setCheckingPayment(true);
        checkResumeAnalysisPayment(sessionToken)
          .then((res) => { if (res.has_paid) { setHasPaid(true); setShowPaywall(false); } })
          .finally(() => setCheckingPayment(false));
      } else {
        setHasPaid(true);
        setShowPaywall(false);
      }
    }
  }, [isOpen, dispatch, requiresPayment, sessionToken, loadingSubscription, subscriptionData, analysisTier]);

// AFTER - reads the correct wallet based on user's country
// Fix - find the wallet matching the user's currency
const getCorrectWallet = () => {
  // balance may be the full API response or the array directly
  const wallets = Array.isArray(balance) ? balance : balance?.data;
  if (!wallets || !wallets.length) return null;
  const targetCurrency = isNigerian ? "NGN" : "USD";
  return wallets.find(w => w.currency === targetCurrency) || null;
};

const getCurrentBalance = () => {
  const wallet = getCorrectWallet();
  return wallet ? parseFloat(wallet.available) : 0; 
};

const getBookBalance = () => {
  const wallet = getCorrectWallet();
  return wallet ? parseFloat(wallet.balance) : 0;
};
  const getTotalBalance   = () => getCurrentBalance() + getBookBalance();

  const handleScroll = useCallback(() => {
    if (!requiresPayment || hasPaid || hasSubscriptionAccess || paywallTriggered.current || !contentRef.current || !blurredRef.current) return;
    const containerRect = contentRef.current.getBoundingClientRect();
    const blurredRect   = blurredRef.current.getBoundingClientRect();
    if (blurredRect.top < containerRect.bottom - 60) {
      paywallTriggered.current = true;
      setShowPaywall(true);
    }
  }, [hasPaid, hasSubscriptionAccess, requiresPayment]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll, isOpen]);

  useEffect(() => {
    if (isOpen) {
      paywallTriggered.current = false;
      setShowPaywall(false);
    } else {
      if (sessionToken) invalidateResumeAnalysisSession(sessionToken);
      setSessionToken(null);
      setHasPaid(false);
      setHasSubscriptionAccess(false);
    }
  }, [isOpen, sessionToken]);

  if (!isOpen || !analysisData) return null;

  const data = deepDecode(analysisData);
  const { job_family, job_level, overall_score, sectional_grades, top_fixes,
          rewrite_suggestions, semantic_recommendations, ats_findings } = data;

  // Determine subscription tier and access levels
  // Use analysisTier (from index.js, set before deep scan deduction) to avoid stale data
  const tier = analysisTier || subscriptionData?.tier || "freemium";
  const isProHunter = tier === "pro_hunter";
  const isSprintPack = tier === "sprint_pack";
  const isFreemium = tier === "freemium" || (!subscriptionData && !analysisTier);

  // Access control based on tier:
  // Freemium: Only score visible, everything else blurred (unless they paid once)
  // Sprint Pack: Score + Sectional Grades + Top Fixes visible, rest blurred
  // Pro Hunter: Everything visible
  // hasPaid: Freemium user paid for this report — grant full access to all sections
  const canSeeSectionalGrades = hasPaid || isSprintPack || isProHunter;
  const canSeeTopFixes = hasPaid || isSprintPack || isProHunter;
  const canSeeRewriteSuggestions = hasPaid || isProHunter;      // Sprint Pack cannot see this
  const canSeeSemanticRecommendations = hasPaid || isProHunter; // Sprint Pack cannot see this
  const canSeeATSCheck = hasPaid || isProHunter;                // Sprint Pack cannot see this

  // Fake placeholder data for freemium locked sections
  // When backend returns empty data for low reasoning, show fake content blurred
  const FAKE_GRADES = { experience: "B", skills: "A", education: "B", summary: "C", formatting: "A" };
  const FAKE_TOP_FIXES = [
    "Add quantifiable achievements to your experience section to demonstrate impact.",
    "Include relevant keywords from the job description in your skills section.",
    "Improve your professional summary to better highlight your unique value proposition.",
    "Use action verbs to start each bullet point in your work experience.",
  ];
  const FAKE_REWRITES = [
    { original: "Responsible for managing a team of five people.", rewrite: "Led a 5-person cross-functional team, delivering projects 20% ahead of schedule.", reason: "Using action verbs and quantifiable metrics makes your impact clearer." },
    { original: "Worked on improving the website performance.", rewrite: "Optimized website load time by 35%, reducing bounce rate and improving user engagement.", reason: "Specific metrics demonstrate measurable results." },
  ];
  const FAKE_SEMANTIC = [
    "Project Management", "Agile Methodology", "Stakeholder Communication",
    "Data Analysis", "Cross-functional Collaboration", "Strategic Planning",
  ];
  const FAKE_ATS = {
    has_multi_column_layout: false,
    contact_info_placement_issue: true,
    non_standard_characters_detected: false,
    recommendations: [
      "Move contact information to the top of the resume for better ATS parsing.",
      "Avoid using tables or text boxes as they can confuse ATS systems.",
    ],
  };

  // Use fake data when real data is empty and user is locked out
  const displayGrades = (sectional_grades && Object.keys(sectional_grades).length > 0) ? sectional_grades : (!canSeeSectionalGrades ? FAKE_GRADES : {});
  const displayTopFixes = (top_fixes && top_fixes.length > 0) ? top_fixes : (!canSeeTopFixes ? FAKE_TOP_FIXES : []);
  const displayRewrites = (rewrite_suggestions && rewrite_suggestions.length > 0) ? rewrite_suggestions : (!canSeeRewriteSuggestions ? FAKE_REWRITES : []);
  const displaySemantic = (semantic_recommendations && semantic_recommendations.length > 0) ? semantic_recommendations : (!canSeeSemanticRecommendations ? FAKE_SEMANTIC : []);
  const displayATS = ats_findings ? ats_findings : (!canSeeATSCheck ? FAKE_ATS : null);

  const getScoreColor = (score) => score >= 80 ? "#10b981" : score >= 60 ? "#f59e0b" : "#ef4444";

  const handleRequestProfessionalReview = async () => {
    if (reviewRequested) {
      showModalToast("info", "You've already requested a professional review. Our team will contact you soon.");
      return;
    }
    setRequestingReview(true);
    try {
      const result = await requestProfessionalResumeReview({
        resume_score: overall_score,
        resume_id: analysisData?.resume_id || userData?.resume || null,
        resume_link: userData?.resume || null,
        analysis_report_link: analysisData?.report_link || null,
      });
      if (result.status) {
        showModalToast("success", "Request sent! Our team will contact you within 24-48 hours with a professional report.", 5000);
        setReviewRequested(true);
      } else {
        showModalToast("error", result.message || "Failed to send request. Please try again.");
      }
    } catch {
      showModalToast("error", "Failed to send request. Please try again.");
    } finally {
      setRequestingReview(false);
    }
  };

 const handlePayClick = async () => {
    const totalBalance = getTotalBalance();

    // 1. Check for insufficient balance
    if (totalBalance < analysisCost) {
      showModalToast(
        "warn",
        `Insufficient balance (${currencySymbol}${numberWithCommas(totalBalance.toFixed(2))}). Redirecting to top up your wallet...`,
        3000
      );

      // Redirect specifically to the Add Funds page after a 3-second delay
      setTimeout(() => {
        history.push("/wallet/add");
      }, 3000);
      return;
    }

    // 2. Proceed with payment if balance is sufficient
    setPaymentLoading(true);
    try {
      const result = await payForResumeAnalysis(dispatch, {
        analysis_type: "resume_analysis",
        resume_id: analysisData?.resume_id || userData?.resume || null,
      });

      if (result.payload?.status) {
        // Success logic
        const token = result.payload?.data?.session_token;
        if (token) setSessionToken(token);

        // Re-run analysis with high reasoning now that user has paid one-time
        showModalToast("info", "Payment successful. Running full analysis...");
        let reAnalyzeResult = { success: true };
        if (onReAnalyze) {
          setReAnalyzing(true);
          reAnalyzeResult = await onReAnalyze("high");
          setReAnalyzing(false);
        }

        setHasPaid(true);
        setShowPaywall(false);
        showModalToast(
          "success",
          reAnalyzeResult?.success
            ? "Payment successful! Full analysis complete."
            : "Payment recorded. The full analysis will update shortly."
        );
      } else {
        // Handle API-side error messages
        const msg = result.payload?.message || result.payload || "Payment failed";
        showModalToast("error", msg);
      }
    } catch (err) {
      // Handle network or unexpected errors
      showModalToast("error", "Payment failed. Please try again.");
    } finally {
      setPaymentLoading(false);
    }
  };

  // ── PDF Download ──────────────────────────────────────────────────────────
  const handleDownload = () => {
    if (!hasPaid && !hasSubscriptionAccess) { setShowPaywall(true); return; }
    const { jsPDF } = window.jspdf;
    if (!jsPDF) { showModalToast("error", "PDF library not loaded. Please refresh and try again."); return; }

    const doc = new jsPDF("p", "mm", "a4");
    const PW  = doc.internal.pageSize.getWidth();
    const PH  = doc.internal.pageSize.getHeight();
    const M   = 12;
    const CW  = PW - M * 2;
    const LH  = 5;
    let y     = 0;

    const txt  = (text, maxW, fs) => { doc.setFontSize(fs); return doc.splitTextToSize(cleanForPdf(text), maxW); };
    const pb   = (need) => { if (y + need > PH - M) { doc.addPage(); y = M + 4; } };
    const gap  = (n = 4) => { y += n; };

    const badge = (label, bx, by, bw, bh, bg, fg, fs) => {
      doc.setFillColor(...bg);
      doc.roundedRect(bx, by, bw, bh, bh / 2, bh / 2, "F");
      doc.setFontSize(fs); doc.setFont("helvetica", "bold"); doc.setTextColor(...fg);
      doc.text(label, bx + bw / 2, by + bh / 2 + fs * 0.18, { align: "center" });
    };

    const cardHeader = (title) => {
      doc.setFillColor(58, 110, 212);
      doc.roundedRect(M, y, 3, 8, 1.5, 1.5, "F");
      doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(30, 30, 30);
      doc.text(title, M + 6, y + 6);
      y += 11;
      doc.setDrawColor(230, 232, 240); doc.setLineWidth(0.25);
      doc.line(M, y, M + CW, y);
      y += 5;
    };

    const bulletLine = (text, maxW, fs = 8.5, dotColor = [58, 110, 212]) => {
      const lines = txt(text, maxW - 6, fs);
      pb(lines.length * LH + 3);
      doc.setFillColor(...dotColor);
      doc.circle(M + 3.5, y + 1.5, 1.1, "F");
      doc.setFontSize(fs); doc.setFont("helvetica", "normal"); doc.setTextColor(55, 55, 55);
      doc.text(lines, M + 7, y);
      y += lines.length * LH + 2;
    };

    const dateStr = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

    // ── HEADER ────────────────────────────────────────────────────────────────
    doc.setFillColor(30, 58, 138);
    doc.rect(0, 0, PW, 18, "F");
    doc.setFillColor(99, 155, 255);
    doc.rect(0, 18, PW, 1.5, "F");
    
    // Workbrook logo (left side) - try to add image if available
    try {
      // Create a temporary image element to load the logo
      const img = new Image();
      img.src = logoImg;
      // Add logo image (12mm width, auto height, positioned at top-left with margin)
      doc.addImage(img, 'SVG', M, 3, 20, 12);
      
      // Text next to logo
      doc.setTextColor(200, 215, 255);
      doc.setFontSize(7); doc.setFont("helvetica", "normal");
      doc.text("Resume Analysis Report", M + 22, 11.5);
    } catch (err) {
      // Fallback to text-only if image fails
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11); doc.setFont("helvetica", "bold");
      doc.text("workbrook", M, 8);
      doc.setFontSize(7); doc.setFont("helvetica", "normal");
      doc.setTextColor(200, 215, 255);
      doc.text("Resume Analysis Report", M, 13.5);
    }
    
    // Date (right side)
    doc.setTextColor(200, 215, 255);
    doc.setFontSize(7.5); doc.setFont("helvetica", "normal");
    doc.text(dateStr, PW - M, 11.5, { align: "right" });
    y = 26;

    // ── SCORE CARD ────────────────────────────────────────────────────────────
    const SCH = 42;
    doc.setFillColor(30, 45, 80);
    doc.roundedRect(M, y, CW, SCH, 4, 4, "F");

    // Ring
    const cx = M + 24, cy = y + SCH / 2, cr = 15;
    const scoreCol = overall_score >= 80 ? [16, 185, 129] : overall_score >= 60 ? [245, 158, 11] : [239, 68, 68];
    // track
    doc.setDrawColor(255, 255, 255); doc.setLineWidth(0.4);
    doc.circle(cx, cy, cr, "S");
    // filled ring in score colour (jsPDF has no arc; simulate with a slightly smaller filled circle)
    doc.setDrawColor(...scoreCol); doc.setLineWidth(3);
    doc.circle(cx, cy, cr, "S");
    // inner fill to create donut
    doc.setFillColor(30, 45, 80);
    doc.circle(cx, cy, cr - 2.5, "F");

    // Score number
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14); doc.setFont("helvetica", "bold");
    doc.text(String(overall_score), cx, cy + 1, { align: "center" });
    doc.setFontSize(6); doc.setFont("helvetica", "normal");
    doc.text("/100", cx, cy + 6, { align: "center" });

    // ── Score label — pill badge BELOW the ring, inside the card ──
    const scoreLabel = overall_score >= 80 ? "Excellent" : overall_score >= 60 ? "Good" : "Needs Work";
    // pill: positioned at cy + cr + 3  (just below ring bottom)
    const labelY = cy + cr + 2;
    // measure text width at fs 6
    doc.setFontSize(6);
    const labelW = doc.getTextWidth(scoreLabel) + 6; // 3px padding each side
    const labelH = 5;
    doc.setFillColor(...scoreCol);
    doc.roundedRect(cx - labelW / 2, labelY, labelW, labelH, labelH / 2, labelH / 2, "F");
    doc.setFont("helvetica", "bold"); doc.setTextColor(255, 255, 255);
    doc.text(scoreLabel, cx, labelY + labelH / 2 + 6 * 0.18, { align: "center" });

    // Meta fields
    const mx    = M + 50;
    const fieldW = (CW - 50 - 8) / 2;
    [[job_family || "N/A", "JOB FAMILY"], [job_level || "N/A", "JOB LEVEL"]].forEach(([val, label], i) => {
      const fx = mx + i * (fieldW + 6);
      doc.setTextColor(150, 175, 215); doc.setFontSize(6.5); doc.setFont("helvetica", "normal");
      doc.text(label, fx, y + 11);
      doc.setTextColor(255, 255, 255); doc.setFontSize(10); doc.setFont("helvetica", "bold");
      const lines = txt(val, fieldW - 2, 10);
      doc.text(lines, fx, y + 18);
    });
    y += SCH + 10;

    // ── SECTIONAL GRADES ──────────────────────────────────────────────────────
    const grades = Object.entries(sectional_grades || {});
    if (canSeeSectionalGrades && grades.length > 0) {
      pb(grades.length * 10 + 22);
      cardHeader("Sectional Grades");
      const colW2 = (CW - 4) / 2;
      grades.forEach(([key, value], i) => {
        const col = i % 2;
        const row = Math.floor(i / 2);
        const gx  = M + col * (colW2 + 4);
        const gy  = y + row * 12;
        pb(13);
        doc.setFillColor(245, 247, 252);
        doc.roundedRect(gx, gy - 1, colW2, 11, 2, 2, "F");

        // Label — leave room for badge (badge is 20mm wide, right-aligned)
        const badgeW = 20;
        const labelMaxW = colW2 - badgeW - 10; // text can use this width
        doc.setFontSize(7.5); doc.setFont("helvetica", "normal"); doc.setTextColor(70, 70, 70);
        const lbl = safe(String(key)).replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
        const lblLines = doc.splitTextToSize(cleanForPdf(lbl), labelMaxW);
        // draw first line only (row height = 11mm)
        doc.text(lblLines[0], gx + 4, gy + 6);

        // Badge — fixed 20mm width so any letter (A/B/C) always fits
        badge(safe(String(value)), gx + colW2 - badgeW - 2, gy + 1.5, badgeW, 7.5,
              [90, 120, 220], [255, 255, 255], 7.5);
      });
      y += Math.ceil(grades.length / 2) * 12 + 4;
      gap(6);
    }

    // ── TOP FIXES ─────────────────────────────────────────────────────────────
    const fixes = top_fixes || [];
    if (canSeeTopFixes && fixes.length > 0) {
      pb(fixes.length * (LH + 2) + 22);
      cardHeader("Top Fixes");
      fixes.forEach(fix => bulletLine(fix, CW));
      gap(6);
    }

    // ── REWRITE SUGGESTIONS ───────────────────────────────────────────────────
    const rewrites = rewrite_suggestions || [];
    if (canSeeRewriteSuggestions && rewrites.length > 0) {
      pb(30);
      cardHeader("Rewrite Suggestions");

      rewrites.forEach((s, i) => {
        const orig   = safe(s.original || "");
        const rw     = safe(s.rewrite  || "");
        const reason = safe(s.reason   || "");

        // ── column layout ──────────────────────────────────────────────────
        // Total usable width: CW
        // [ 4px left pad ][ origPanel colW ][ 8px gap ][ rwPanel colW ][ 4px right pad ]
        // Arrow is drawn as text centered in the gap — NOT as a JSX element,
        const colW   = (CW - 8) / 2;   // equal halves, 8mm total gap
        const textW  = colW - 8;        // inner text width (4mm each side)
        const gapX   = M + 4 + colW;   // x-start of the 8mm gap column

        const oLines  = txt(orig,   textW, 8);
        const rLines  = txt(rw,     textW, 8);
        const rLines2 = txt(reason, CW - 12, 8);

        const panelH  = Math.max(oLines.length, rLines.length) * LH + 16;
        const reasonH = rLines2.length * LH + 12;
        const cardH   = panelH + reasonH + 12;

        pb(Math.min(cardH + 4, PH - 28));
        const cY = y;

        // Card background
        doc.setFillColor(255, 252, 235);
        doc.roundedRect(M, cY, CW, cardH, 3, 3, "F");
        doc.setDrawColor(252, 196, 75); doc.setLineWidth(0.3);
        doc.roundedRect(M, cY, CW, cardH, 3, 3, "S");

        // Number badge
        badge(`#${i + 1}`, M + 4, cY + 4, 14, 6.5, [255, 255, 255], [210, 90, 50], 7);

        const panelY = cY + 14;

        // ORIGINAL panel
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(M + 4, panelY, colW, panelH - 2, 2, 2, "F");
        doc.setFontSize(6); doc.setFont("helvetica", "bold"); doc.setTextColor(160, 160, 160);
        doc.text("ORIGINAL", M + 7, panelY + 5);
        doc.setFontSize(8); doc.setFont("helvetica", "normal"); doc.setTextColor(50, 50, 50);
        doc.text(oLines, M + 6, panelY + 11);

        // Arrow — drawn with doc.text() only, centred in the gap
        // No JSX involved here at all, so no stray characters possible
        doc.setFontSize(12); doc.setTextColor(200, 180, 130);
        doc.text("\u2192", gapX + 4, panelY + panelH / 2);

        // REWRITE panel
        const rwX = gapX + 8;
        const rwW = colW - 4;
        doc.setFillColor(240, 255, 245);
        doc.roundedRect(rwX, panelY, rwW, panelH - 2, 2, 2, "F");
        doc.setFontSize(6); doc.setFont("helvetica", "bold"); doc.setTextColor(16, 140, 80);
        doc.text("REWRITE", rwX + 3, panelY + 5);
        doc.setFontSize(8); doc.setFont("helvetica", "normal"); doc.setTextColor(30, 90, 50);
        doc.text(txt(rw, rwW - 8, 8), rwX + 4, panelY + 11);

        // REASON box
        const rsY = panelY + panelH + 2;
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(M + 4, rsY, CW - 8, reasonH, 2, 2, "F");
        doc.setFillColor(210, 90, 50);
        doc.roundedRect(M + 4, rsY, 2.5, reasonH, 1, 1, "F");
        doc.setFontSize(7.5); doc.setFont("helvetica", "bold"); doc.setTextColor(210, 90, 50);
        doc.text("Reason:", M + 9, rsY + 5.5);
        doc.setFont("helvetica", "normal"); doc.setTextColor(60, 60, 60);
        doc.text(rLines2, M + 28, rsY + 5.5);

        y = cY + cardH + 8;
      });
      gap(4);
    }

    // ── SEMANTIC RECOMMENDATIONS ──────────────────────────────────────────────
    const semItems = semantic_recommendations || [];
    if (canSeeSemanticRecommendations && semItems.length > 0) {
      pb(semItems.length * (LH + 2) + 22);
      cardHeader("Semantic Recommendations");
      semItems.forEach(rec => bulletLine(rec, CW, 8.5, [16, 185, 129]));
      gap(6);
    }

    // ── ATS COMPATIBILITY ─────────────────────────────────────────────────────
    if (canSeeATSCheck && ats_findings) {
      const atsRecs = ats_findings.recommendations || [];
      pb(60);
      cardHeader("ATS Compatibility");

      const checks = [
        { label: "Multi-Column Layout",     issue: ats_findings.has_multi_column_layout,          yes: "Detected",    no: "Not Detected" },
        { label: "Contact Info Placement",  issue: ats_findings.contact_info_placement_issue,     yes: "Issue Found", no: "OK" },
        { label: "Non-Standard Characters", issue: ats_findings.non_standard_characters_detected, yes: "Detected",    no: "Not Detected" },
      ];

      checks.forEach(c => {
        pb(13);
        const bg = c.issue ? [255, 243, 220] : [220, 252, 231];
        const fg = c.issue ? [180, 90, 10]   : [5, 120, 60];
        doc.setFillColor(...bg);
        doc.roundedRect(M, y - 2, CW, 11, 2, 2, "F");
        doc.setFillColor(...fg); doc.circle(M + 5, y + 3.5, 2.5, "F");
        doc.setFontSize(8.5); doc.setFont("helvetica", "normal"); doc.setTextColor(...fg);
        doc.text(`${c.label}: ${c.issue ? c.yes : c.no}`, M + 11, y + 5.5);
        y += 12;
      });
      gap(4);

      if (atsRecs.length > 0) {
        pb(atsRecs.length * (LH + 2) + 16);
        doc.setFillColor(235, 238, 252);
        const recBlockH = atsRecs.length * (LH + 3) + 12;
        doc.roundedRect(M, y, CW, recBlockH, 2, 2, "F");
        doc.setFillColor(99, 126, 234);
        doc.roundedRect(M, y, 3, recBlockH, 1, 1, "F");
        doc.setFontSize(8.5); doc.setFont("helvetica", "bold"); doc.setTextColor(80, 100, 200);
        doc.text("ATS Recommendations:", M + 6, y + 7);
        y += 11;
        atsRecs.forEach(rec => {
          const lines = txt(rec, CW - 14, 8);
          pb(lines.length * LH + 3);
          doc.setFontSize(8); doc.setFont("helvetica", "normal");
          doc.setTextColor(99, 126, 234); doc.text("\u2192", M + 5, y);
          doc.setTextColor(55, 55, 55); doc.text(lines, M + 11, y);
          y += lines.length * LH + 2;
        });
        y += 4;
      }
    }

    // ── FOOTER ────────────────────────────────────────────────────────────────
    doc.setDrawColor(200, 205, 220); doc.setLineWidth(0.25);
    doc.line(M, y + 4, M + CW, y + 4);
    doc.setFontSize(7.5); doc.setTextColor(160, 165, 175); doc.setFont("helvetica", "normal");
    doc.text(`Generated by workbrook  |  ${dateStr}`, PW / 2, y + 9, { align: "center" });

    doc.save(`resume-analysis-report-${Date.now()}.pdf`);
    showModalToast("success", "PDF downloaded successfully!");
  };
  // ── End PDF ───────────────────────────────────────────────────────────────

  return ReactDOM.createPortal(
    <>
      <div className="resume-analysis-overlay" onClick={onClose}></div>
      <div className="resume-analysis-modal">
        <div className="modal-header">
          <h2>Resume Analysis Report</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-content" ref={contentRef}>

          {/* Re-analysis loading overlay */}
          {reAnalyzing && (
            <div style={{
              position: "absolute",
              top: 0, left: 0, right: 0, bottom: 0,
              background: "rgba(255,255,255,0.85)",
              backdropFilter: "blur(2px)",
              zIndex: 999,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "1rem",
            }}>
              <div className="reanalyze-spinner" style={{
                width: 48, height: 48,
                border: "4px solid #e2e8f0",
                borderTopColor: "#667eea",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
              }} />
              <p style={{ fontSize: "0.95rem", color: "#475569", fontWeight: 600 }}>
                Running full analysis...
              </p>
            </div>
          )}

          {/* ── Score Card ── */}
          <div className="score-card">
            <div className="score-circle-container">
              <svg
                className="score-circle"
                viewBox="0 0 120 120"
                xmlns="http://www.w3.org/2000/svg"
                style={{ display: "block", overflow: "visible" }}
              >
                {/* track */}
                <circle cx="60" cy="60" r="50" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="8" />
                {/* progress */}
                <circle
                  cx="60" cy="60" r="50"
                  fill="none"
                  stroke={getScoreColor(overall_score)}
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 50}`}
                  strokeDashoffset={`${2 * Math.PI * 50 * (1 - overall_score / 100)}`}
                  style={{
                    transform: "rotate(-90deg)",
                    transformOrigin: "60px 60px",
                    transition: "stroke-dashoffset 1.1s cubic-bezier(0.4,0,0.2,1)",
                  }}
                />
              </svg>

              {/* centred number — lives inside the ring, never overlaps badge */}
              <div className="score-text">
                <span className="score-value">{overall_score}</span>
                <span className="score-max">/100</span>
              </div>

              {/* badge sits BELOW the svg via absolute bottom: -18px */}
              <div
                className="score-label-badge"
                style={{ backgroundColor: getScoreColor(overall_score) }}
              >
                {overall_score >= 80 ? "Excellent" : overall_score >= 60 ? "Good" : "Needs Work"}
              </div>
            </div>

            <div className="score-details">
              <div className="detail-item">
                <span className="detail-label">Job Family</span>
                <span className="detail-value">{job_family}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Job Level</span>
                <span className="detail-value">{job_level}</span>
              </div>
            </div>
          </div>

          {/* Professional Review CTA */}
          {hasPaid && (
            <div className="professional-review-cta">
              <div className="cta-content">
                <div className="cta-icon">👨‍💼</div>
                <div className="cta-text">
                  <h4>Want a Professional Review?</h4>
                  <p>Get expert feedback and a detailed professional report from our recruitment specialists.</p>
                </div>
              </div>
              <button
                className={`cta-button ${reviewRequested ? "requested" : ""}`}
                onClick={handleRequestProfessionalReview}
                disabled={requestingReview || reviewRequested}
              >
                {requestingReview ? "Sending..." : reviewRequested ? "✓ Request Sent" : "Request Professional Review"}
              </button>
            </div>
          )}

          {/* Locked / unlocked content */}
          <div
            ref={blurredRef}
            className={requiresPayment && !hasPaid && !hasSubscriptionAccess ? "locked-content" : ""}
            style={{ position: "relative" }}
          >
            {/* Sectional Grades - Visible for Sprint Pack & Pro Hunter */}
            {canSeeSectionalGrades ? (
              <div className="section">
                <h3 className="section-title">Sectional Grades</h3>
                {/* ... (rest of the code remains the same) */}
                <div className="grades-grid">
                  {Object.entries(displayGrades).map(([key, value]) => (
                    <div key={key} className="grade-item">
                      <div className="grade-label">{safe(key).replace(/_/g, " ").toUpperCase()}</div>
                      <div className="grade-badge">{safe(value)}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="section locked-content" style={{ position: "relative" }}>
                <h3 className="section-title">Sectional Grades</h3>
                <div className="grades-grid">
                  {Object.entries(displayGrades).map(([key, value]) => (
                    <div key={key} className="grade-item">
                      <div className="grade-label">{safe(key).replace(/_/g, " ").toUpperCase()}</div>
                      <div className="grade-badge">{safe(value)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Top Fixes - Visible for Sprint Pack & Pro Hunter */}
            {canSeeTopFixes ? (
              <div className="section">
                <h3 className="section-title"><span className="icon">🔧</span> Top Fixes</h3>
                <ul className="fixes-list">
                  {displayTopFixes.map((fix, idx) => (
                    <li key={idx} className="fix-item">{safe(fix)}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="section locked-content" style={{ position: "relative" }}>
                <h3 className="section-title"><span className="icon">🔧</span> Top Fixes</h3>
                <ul className="fixes-list">
                  {displayTopFixes.map((fix, idx) => (
                    <li key={idx} className="fix-item">{safe(fix)}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Rewrite Suggestions */}
            <div className={`section ${!canSeeRewriteSuggestions ? "locked-content" : ""}`} style={{ position: "relative" }}>
              <h3 className="section-title"><span className="icon">✍️</span> Rewrite Suggestions</h3>
              <div className="rewrite-suggestions">
                {displayRewrites.map((suggestion, idx) => (
                  <div key={idx} className="suggestion-card">
                    <div className="suggestion-header">
                      <span className="suggestion-number">#{idx + 1}</span>
                    </div>
                    <div className="suggestion-content">
                      <div className="suggestion-block original">
                        <div className="block-label">Original</div>
                        <div className="block-text">{safe(suggestion.original)}</div>
                      </div>
                      <div className="suggestion-block rewrite">
                        <div className="block-label">Rewrite</div>
                        <div className="block-text">{safe(suggestion.rewrite)}</div>
                      </div>
                    </div>
                    <div className="suggestion-reason">
                      <strong>Reason:</strong> {safe(suggestion.reason)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Semantic Recommendations - Only visible for Pro Hunter */}
            <div className={`section ${!canSeeSemanticRecommendations ? "locked-content" : ""}`} style={{ position: "relative" }}>
              <h3 className="section-title"><span className="icon">💡</span> Semantic Recommendations</h3>
              <ul className="recommendations-list">
                {displaySemantic.map((rec, idx) => (
                  <li key={idx} className="recommendation-item">{safe(rec)}</li>
                ))}
              </ul>
            </div>

            {/* ATS Findings - Only visible for Pro Hunter */}
            {displayATS && (
              <div className={`section ${!canSeeATSCheck ? "locked-content" : ""}`} style={{ position: "relative" }}>
                <h3 className="section-title"><span className="icon">🤖</span> ATS Compatibility Check</h3>
                <div className="ats-checks">
                  <div className={`ats-check ${displayATS.has_multi_column_layout ? "warning" : "success"}`}>
                    <span className="check-icon">{displayATS.has_multi_column_layout ? "⚠️" : "✓"}</span>
                    <span>Multi-Column Layout: {displayATS.has_multi_column_layout ? "Detected" : "Not Detected"}</span>
                  </div>
                  <div className={`ats-check ${displayATS.contact_info_placement_issue ? "warning" : "success"}`}>
                    <span className="check-icon">{displayATS.contact_info_placement_issue ? "⚠️" : "✓"}</span>
                    <span>Contact Info Placement: {displayATS.contact_info_placement_issue ? "Issue Found" : "OK"}</span>
                  </div>
                  <div className={`ats-check ${displayATS.non_standard_characters_detected ? "warning" : "success"}`}>
                    <span className="check-icon">{displayATS.non_standard_characters_detected ? "⚠️" : "✓"}</span>
                    <span>Non-Standard Characters: {displayATS.non_standard_characters_detected ? "Detected" : "Not Detected"}</span>
                  </div>
                </div>
                {displayATS.recommendations && displayATS.recommendations.length > 0 && (
                  <div className="ats-recommendations">
                    <h4 className="ats-rec-title">ATS Recommendations:</h4>
                    <ul className="ats-rec-list">
                      {displayATS.recommendations.map((rec, idx) => (
                        <li key={idx} className="ats-rec-item">{safe(rec)}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Paywall - For Freemium users and Sprint Pack users with no credits */}
        {requiresPayment && showPaywall && !hasPaid && !hasSubscriptionAccess && (
          <div className="paywall-overlay" onClick={() => setShowPaywall(false)}>
            <div className="paywall-modal" onClick={(e) => e.stopPropagation()}>
              <div className="paywall-icon">🔒</div>
              <h3 className="paywall-title">Unlock Full Report</h3>
              <p className="paywall-description">
                Get detailed fixes, rewrite suggestions, semantic recommendations,
                and ATS compatibility insights to improve your resume.
              </p>
              <ul className="paywall-features">
                <li>🔧 Top Fixes &amp; Action Items</li>
                <li>✍️ AI-Powered Rewrite Suggestions</li>
                <li>💡 Semantic Recommendations</li>
                <li>🤖 Full ATS Compatibility Report</li>
                <li>⬇️ Downloadable PDF Report</li>
              </ul>
              <div className="paywall-balance" style={{ width: "100%", textAlign: "center" }}>
                <p style={{ marginBottom: "0.5rem", fontSize: "0.9rem", color: "#555", fontWeight: 600 }}>Option 1: Subscribe for unlimited access</p>
                <a href="/my-subscriptions" style={{ 
                  display: "inline-block",
                  padding: "0.6rem 1.25rem",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "#fff",
                  borderRadius: "8px",
                  textDecoration: "none",
                  fontWeight: "600",
                  fontSize: "0.9rem",
                  marginBottom: "1rem"
                }}>
                  View Subscription Plans
                </a>
                <p style={{ marginBottom: "0.5rem", fontSize: "0.9rem", color: "#555", fontWeight: 600 }}>Option 2: Pay once for this report</p>
                <p style={{ margin: "0.2rem 0", fontSize: "0.85rem" }}>Book balance: <strong>{currencySymbol}{numberWithCommas(getBookBalance().toFixed(2))}</strong></p>
                <p style={{ margin: "0.2rem 0", fontSize: "0.85rem" }}>Available balance: <strong>{currencySymbol}{numberWithCommas(getCurrentBalance().toFixed(2))}</strong></p>
                <p style={{ margin: "0.2rem 0", fontSize: "0.85rem" }}>Total balance: <strong>{currencySymbol}{numberWithCommas(getTotalBalance().toFixed(2))}</strong></p>
                <p style={{ margin: "0.4rem 0 0", fontSize: "0.85rem" }}>Cost: <strong>{currencySymbol}{numberWithCommas(analysisCost.toFixed(2))} {currencyLabel}</strong></p>
              </div>
              <button className="paywall-pay-btn" onClick={handlePayClick} disabled={paymentLoading}>
                {paymentLoading
                  ? "Processing..."
                  : getTotalBalance() >= analysisCost
                    ? `Pay ${currencySymbol}${numberWithCommas(analysisCost.toFixed(2))} from Wallet`
                    : `Top Up Wallet to Pay ${currencySymbol}${numberWithCommas(analysisCost.toFixed(2))}`
                }
              </button>
              <button className="paywall-dismiss-btn" onClick={() => setShowPaywall(false)}>Maybe Later</button>
            </div>
          </div>
        )}

        <div className="modal-footer">
          {!requiresPayment || hasPaid || hasSubscriptionAccess ? (
            <>
              <button className="download-btn" onClick={handleDownload}>
                <span className="btn-icon">⬇</span>Download Report
              </button>
              <button
                className={`professional-review-footer-btn ${reviewRequested ? "requested" : ""}`}
                onClick={handleRequestProfessionalReview}
                disabled={requestingReview || reviewRequested}
              >
                <span className="btn-icon">👨‍💼</span>
                {requestingReview ? "Sending..." : reviewRequested ? "✓ Review Requested" : "Request Professional Review"}
              </button>
              <button className="close-footer-btn" onClick={onClose}>Close</button>
            </>
          ) : (
            <>
              <button className="pay-footer-btn" onClick={() => setShowPaywall(true)} disabled={paymentLoading}>
                <span className="btn-icon">🔓</span>
                Pay {currencySymbol}{numberWithCommas(analysisCost.toFixed(2))} to Download &amp; View Full Report
              </button>
              <button className="close-footer-btn" onClick={onClose}>Close</button>
            </>
          )}
        </div>
      </div>

      {/* Toast */}
      {modalToast.show && (
        <div className={`modal-toast modal-toast-${modalToast.type}`}>
          <div className="modal-toast-content">
            <span className="modal-toast-icon">
              {modalToast.type === "success" ? "✓" : modalToast.type === "error" ? "✕" : modalToast.type === "warn" ? "⚠" : "ℹ"}
            </span>
            <span className="modal-toast-message">{modalToast.message}</span>
            <button className="modal-toast-close" onClick={() => setModalToast({ show: false, message: "", type: "info" })}>×</button>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default ResumeAnalysisModal;